import { WorkflowActionKind, WorkflowConditionOp, WorkflowTrigger } from '@/services/workflows.service';

export const TRIGGER_LABELS: Record<WorkflowTrigger, string> = {
  'lead.created':         'A new lead arrives',
  'lead.status_changed':  'A lead\'s status changes',
  'message.received':     'A customer message arrives (WhatsApp or email)',
  'deal.created':         'A deal is created',
  'deal.stage_changed':   'A deal moves to another stage',
  'document.paid':        'An invoice is marked paid',
};

export const ACTION_LABELS: Record<WorkflowActionKind, string> = {
  send_template: 'Send an approved WhatsApp template',
  create_task:   'Create a follow-up task',
  assign_owner:  'Assign an owner',
  update_field:  'Update the lead',
  delay:         'Wait',
  condition:     'Continue only if…',
  send_webhook:  'Send the event to a webhook',
  branch:        'If / else branch (API only)',
};

/** Kinds offered in the editor; `branch` stays readable but is built via the API. */
export const EDITABLE_ACTIONS: WorkflowActionKind[] = ['send_template', 'create_task', 'assign_owner', 'update_field', 'delay', 'condition', 'send_webhook'];

export const LEAD_STATUS_OPTIONS = [
  { value: 'new', label: 'New' }, { value: 'contacted', label: 'Contacted' }, { value: 'qualified', label: 'Qualified' },
  { value: 'won', label: 'Won' }, { value: 'lost', label: 'Lost' }, { value: 'spam', label: 'Spam' },
];

export const LEAD_SOURCE_OPTIONS = [
  { value: 'ctwa', label: 'Click-to-WhatsApp ad' }, { value: 'lead_ad_form', label: 'Meta lead form' },
  { value: 'tiktok_lead_ad', label: 'TikTok lead form' }, { value: 'google_lead_form', label: 'Google lead form' },
  { value: 'google_ads', label: 'Google ad' }, { value: 'inbound_keyword', label: 'WhatsApp message' },
  { value: 'web_form', label: 'Web form' }, { value: 'email', label: 'Email' }, { value: 'manual', label: 'Added manually' },
];

export type FieldKind = 'enum' | 'text' | 'number' | 'bool' | 'user' | 'stage' | 'pipeline';
export interface FieldOption { value: string; label: string; kind: FieldKind; options?: Array<{ value: string; label: string }> }

const CURRENT_LEAD: FieldOption[] = [
  { value: 'lead.status', label: 'Lead status (now)', kind: 'enum', options: LEAD_STATUS_OPTIONS },
  { value: 'lead.source', label: 'Lead source', kind: 'enum', options: LEAD_SOURCE_OPTIONS },
  { value: 'lead.assignedToUserUid', label: 'Lead owner (now)', kind: 'user' },
  { value: 'lead.hasPhone', label: 'Lead has a phone number', kind: 'bool' },
  { value: 'lead.hasEmail', label: 'Lead has an email', kind: 'bool' },
  { value: 'lead.hoursSinceLastInbound', label: 'Hours since the customer last messaged', kind: 'number' },
  { value: 'lead.hoursSinceCreated', label: 'Hours since the lead arrived', kind: 'number' },
  { value: 'lead.estimatedValueAmount', label: 'Lead estimated value', kind: 'number' },
];

const CURRENT_DEAL: FieldOption[] = [
  { value: 'deal.outcome', label: 'Deal outcome (now)', kind: 'enum', options: [{ value: 'open', label: 'Open' }, { value: 'won', label: 'Won' }, { value: 'lost', label: 'Lost' }] },
  { value: 'deal.stageUid', label: 'Deal stage (now)', kind: 'stage' },
  { value: 'deal.amount', label: 'Deal amount (now)', kind: 'number' },
  { value: 'deal.ownerUserUid', label: 'Deal owner (now)', kind: 'user' },
];

export const FIELDS_BY_TRIGGER: Record<WorkflowTrigger, FieldOption[]> = {
  'lead.created': [
    { value: 'source', label: 'Source', kind: 'enum', options: LEAD_SOURCE_OPTIONS },
    { value: 'sourceCampaignId', label: 'Campaign ID', kind: 'text' },
    { value: 'sourceAdId', label: 'Ad ID', kind: 'text' },
    { value: 'email', label: 'Email', kind: 'text' },
    { value: 'phone', label: 'Phone', kind: 'text' },
    ...CURRENT_LEAD,
  ],
  'lead.status_changed': [
    { value: 'to', label: 'New status', kind: 'enum', options: LEAD_STATUS_OPTIONS },
    { value: 'from', label: 'Previous status', kind: 'enum', options: LEAD_STATUS_OPTIONS },
    { value: 'assignedToUserUid', label: 'Owner', kind: 'user' },
    ...CURRENT_LEAD,
  ],
  'message.received': [
    { value: 'channel', label: 'Channel', kind: 'enum', options: [{ value: 'whatsapp', label: 'WhatsApp' }, { value: 'email', label: 'Email' }] },
    { value: 'body', label: 'Message text', kind: 'text' },
    { value: 'mediaType', label: 'Media type (image, audio…)', kind: 'text' },
    { value: 'assignedToUserUid', label: 'Owner', kind: 'user' },
    ...CURRENT_LEAD,
  ],
  'deal.created': [
    { value: 'pipelineUid', label: 'Pipeline', kind: 'pipeline' },
    { value: 'stageUid', label: 'Stage', kind: 'stage' },
    { value: 'amount', label: 'Amount', kind: 'number' },
    { value: 'ownerUserUid', label: 'Owner', kind: 'user' },
    ...CURRENT_DEAL,
  ],
  'deal.stage_changed': [
    { value: 'stageUid', label: 'New stage', kind: 'stage' },
    { value: 'previousStageUid', label: 'Previous stage', kind: 'stage' },
    { value: 'terminal', label: 'New stage outcome', kind: 'enum', options: [{ value: 'active', label: 'Open' }, { value: 'won', label: 'Won' }, { value: 'lost', label: 'Lost' }] },
    { value: 'amount', label: 'Amount', kind: 'number' },
    { value: 'ownerUserUid', label: 'Owner', kind: 'user' },
    ...CURRENT_DEAL,
  ],
  'document.paid': [
    { value: 'type', label: 'Document type', kind: 'text' },
    { value: 'amount', label: 'Amount', kind: 'number' },
    { value: 'paidAmount', label: 'Paid amount', kind: 'number' },
    { value: 'paymentMethod', label: 'Payment method', kind: 'text' },
  ],
};

export const OPS_BY_KIND: Record<FieldKind, Array<{ value: WorkflowConditionOp; label: string }>> = {
  enum:     [{ value: 'eq', label: 'is' }, { value: 'ne', label: 'is not' }],
  user:     [{ value: 'eq', label: 'is' }, { value: 'ne', label: 'is not' }, { value: 'exists', label: 'is set' }, { value: 'notExists', label: 'is empty' }],
  stage:    [{ value: 'eq', label: 'is' }, { value: 'ne', label: 'is not' }],
  pipeline: [{ value: 'eq', label: 'is' }, { value: 'ne', label: 'is not' }],
  text:     [{ value: 'contains', label: 'contains' }, { value: 'eq', label: 'is exactly' }, { value: 'startsWith', label: 'starts with' }, { value: 'exists', label: 'is set' }, { value: 'notExists', label: 'is empty' }],
  number:   [{ value: 'gt', label: 'is more than' }, { value: 'lt', label: 'is less than' }, { value: 'eq', label: 'equals' }],
  bool:     [{ value: 'exists', label: 'is yes' }, { value: 'notExists', label: 'is no' }],
};

export const VARIABLE_SOURCES = [
  { value: 'lead.first_name', label: 'Lead first name' },
  { value: 'lead.name', label: 'Lead full name' },
  { value: 'lead.phone', label: 'Lead phone' },
  { value: 'lead.email', label: 'Lead email' },
  { value: 'company.name', label: 'Your business name' },
  { value: 'text', label: 'Fixed text' },
];

/** `{{lead.first_name|there}}` ⇄ { source, fallback }; anything else is fixed text. */
export function parseVariable(value: unknown): { source: string; text: string } {
  const raw = typeof value === 'string' ? value : '';
  const match = raw.match(/^\{\{\s*([a-z_.]+)\s*(?:\|([^}]*))?\}\}$/);
  if (match && VARIABLE_SOURCES.some(source => source.value === match[1])) return { source: match[1], text: match[2] ?? '' };
  return { source: 'text', text: raw };
}

export function composeVariable(source: string, text: string): string {
  if (source === 'text') return text;
  return text.trim() ? `{{${source}|${text.trim()}}}` : `{{${source}}}`;
}
